import React from "react";
import {
  Container,
  Typography,
  Divider,
  Link,
  Box,
  Stack,
} from "@mui/material";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import { useParams } from "react-router-dom";

const TermsConditions = () => {
  const { id } = useParams();
  const showLayout = id === "1";

  const sections = [
    {
      number: "01",
      title: "Introduction",
      content:
        "Hindustan Wardrobe is a platform designed to facilitate the renting of dresses between users and renters. Our goal is to provide a seamless and trustworthy experience for all parties involved.",
    },
    {
      number: "02",
      title: "Account Registration and Usage",
      subsections: [
        {
          title: "2.1 Eligibility",
          items: [
            "You must be at least 18 years old to use our platform.",
            "By registering, you confirm that the information provided is accurate, complete, and up to date.",
          ],
        },
        {
          title: "2.2 Account Responsibilities",
          items: [
            "You are responsible for maintaining the confidentiality of your account credentials.",
            "Any unauthorized use of your account must be reported immediately to Hindustan Wardrobe.",
            "Hindustan Wardrobe will not be liable for any loss due to unauthorized access.",
          ],
        },
      ],
    },
    {
      number: "03",
      title: "Listing Items for Rent",
      subsections: [
        {
          title: "3.1 Product Listing Guidelines",
          items: [
            "Renters can list items by uploading clear photos, descriptions, and pricing details.",
            "The listed items must be clean, in good condition, and accurately described.",
            "The renter must specify the rental price, duration, and any additional services such as fitting sessions or trial sessions.",
          ],
        },
        {
          title: "3.2 Prohibited Items",
          intro: "Renters are not allowed to list:",
          items: [
            "Damaged, torn, or stained clothing.",
            "Counterfeit or stolen items.",
            "Items that violate any intellectual property rights.",
            "Items deemed inappropriate by Hindustan Wardrobe.",
          ],
        },
      ],
    },
    {
      number: "04",
      title: "Booking and Reservation Process",
      subsections: [
        {
          title: "4.1 Discovering and Reserving Items",
          items: [
            "Users can browse and filter products based on category, size, price, and location.",
            "A booking is confirmed only after the renter accepts the request within 48 hours.",
            "Users can opt for fitting or try-on sessions before renting.",
          ],
        },
        {
          title: "4.2 Cancellation and Refund Policy",
          items: [
            "User-Initiated Cancellation Before Renter Acceptance: If a user cancels a booking before the renter accepts it, the full booking amount will be refunded to the user, minus the transaction fee charged by our payment gateway, Razorpay. Razorpay charges a transaction fee of 2% on the transaction amount, plus 18% GST on this fee, totaling 2.36%. For example, on a ₹1,000 payment, the transaction fee would be ₹23.60. This fee is non-refundable and will be deducted from the refund amount.",
            "Renter-Initiated Cancellation: If a renter cancels an order, the user will receive a full refund.",
            "Repeated Cancellations: If a renter repeatedly cancels orders, their account may be flagged or suspended.",
          ],
        },
      ],
    },
    {
      number: "05",
      title: "Delivery and Return Process",
      subsections: [
        {
          title: "5.1 Delivery and Pickup",
          items: [
            "User and Renter Responsibility: Hindustan Wardrobe does not provide delivery or pickup services. It is the sole responsibility of the user and the renter to coordinate and manage the delivery and return of rented items.",
            "Mutual Agreement: Both parties must agree on a convenient time and location for the exchange of items.",
            "Verification: At the time of delivery and return, both parties should verify the condition of the item and confirm the transaction through the platform.",
          ],
        },
        {
          title: "5.2 Returning Rented Items",
          items: [
            "Users must return the items in the same condition as received.",
            "Both the user and renter must verify the product condition and confirm via a 4-digit OTP at the time of pickup.",
          ],
        },
        {
          title: "5.3 Late Returns & Penalties",
          items: [
            "Users failing to return the item on time will be charged a daily late fee equal to the per-day rental fee.",
            "If an item is not returned within 3 days, it will be considered lost, and the full security deposit will be forfeited.",
          ],
        },
      ],
    },
    {
      number: "06",
      title: "Owner Inspection and Security Deposit",
      subsections: [
        {
          title: "6.1 Inspection Guidelines",
          items: [
            "Renters will inspect returned items to ensure they are in the same condition.",
            "Any damages, stains, or missing accessories may lead to partial or full deduction of the security deposit.",
          ],
        },
        {
          title: "6.2 Refund Process",
          items: [
            "If the item is returned in good condition, the security deposit will be refunded within 24–48 hours.",
            "If a dispute arises, Hindustan Wardrobe will mediate based on product images and renter feedback.",
          ],
        },
      ],
    },
    {
      number: "07",
      title: "Payments and Billing",
      subsections: [
        {
          title: "7.1 Payment Methods",
          items: [
            "Hindustan Wardrobe accepts UPI, credit/debit cards, digital wallets, and net banking.",
            "Payments must be made at the time of booking to secure the rental.",
          ],
        },
        {
          title: "7.2 Security Deposit",
          items: [
            "A refundable security deposit is required for each rental item.",
            "The deposit is refunded after successful return and inspection of the product.",
          ],
        },
        {
          title: "7.3 Service Charges",
          items: [
            "Hindustan Wardrobe may charge a service fee for platform maintenance and customer support.",
            "The exact service charge will be displayed before payment confirmation.",
          ],
        },
      ],
    },
    {
      number: "08",
      title: "Feedback, Ratings, and Community Guidelines",
      subsections: [
        {
          title: "8.1 Feedback System",
          items: [
            "Users and renters can rate each other and leave honest reviews.",
            "Hindustan Wardrobe reserves the right to moderate or remove reviews if they contain offensive language or false claims.",
          ],
        },
        {
          title: "8.2 Community Standards",
          items: [
            "Users must respect each other and maintain professionalism while interacting.",
            "Any form of abuse, harassment, or fraud will result in account suspension or termination.",
          ],
        },
      ],
    },
    {
      number: "09",
      title: "User Responsibilities",
      items: [
        "Users must handle rented items carefully and return them on time.",
        "Users are liable for any damage, loss, or theft of the rented item.",
        "Users must comply with all applicable laws and regulations while using our services.",
      ],
    },
    {
      number: "10",
      title: "Intellectual Property",
      items: [
        "All content, trademarks, logos, and materials on Hindustan Wardrobe are owned by the company or its licensors.",
        "Users cannot copy, reproduce, or distribute any content from the platform without permission.",
      ],
    },
    {
      number: "11",
      title: "Privacy and Data Protection",
      items: [
        "Hindustan Wardrobe respects user privacy and follows strict data protection policies.",
        "Personal data collected during account registration and transactions is stored securely.",
        "The platform does not share personal data with third parties without user consent.",
      ],
    },
    {
      number: "12",
      title: "Account Suspension & Termination",
      subsections: [
        {
          title: "12.1 Grounds for Suspension",
          intro:
            "Hindustan Wardrobe reserves the right to suspend or terminate an account if:",
          items: [
            "The user violates any terms and conditions.",
            "There is suspicion of fraudulent activity.",
            "The user engages in harassment, abuse, or illegal conduct.",
          ],
        },
        {
          title: "12.2 User-Initiated Termination",
          items: [
            "Users can request account deletion by filling out the Account Deletion Form.",
            "Upon submission, the account deletion request will be processed within 7 days.",
            "Any pending transactions must be completed before account closure.",
          ],
        },
      ],
    },
    {
      number: "13",
      title: "Liability and Indemnification",
      items: [
        "Hindustan Wardrobe is not liable for any damages, losses, or disputes arising from user-to-user transactions.",
        "Users agree to indemnify and hold harmless Hindustan Wardrobe from any claims or damages resulting from their actions on the platform.",
      ],
    },
    {
      number: "14",
      title: "Modifications to Terms",
      items: [
        "Hindustan Wardrobe reserves the right to update these terms and conditions at any time.",
        "Users will be notified of major changes via email or platform notifications.",
        "Continued use of the platform after changes implies acceptance of the new terms.",
      ],
    },
  ];

  const renderItems = (items) => (
    <Stack spacing={1.5} sx={{ mt: 2 }}>
      {items.map((item) => (
        <Box
          key={item}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 5,
              height: 5,
              minWidth: 5,
              borderRadius: "50%",
              background: "#977743",
              mt: "9px",
            }}
          />

          <Typography
            sx={{
              color: "#625E58",
              fontSize: 15,
              lineHeight: 1.85,
            }}
          >
            {item}
          </Typography>
        </Box>
      ))}
    </Stack>
  );

  return (
    <>
      {showLayout && <Navbar />}

      <Box
        sx={{
          background: "#F7F4EF",
          minHeight: "100vh",
          color: "#171717",
        }}
      >
        <Box
          sx={{
            borderBottom: "1px solid #DDD7CE",
            py: { xs: 7, md: 10 },
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              width: 340,
              height: 340,
              border: "1px solid rgba(151,119,67,0.12)",
              borderRadius: "50%",
              right: { xs: -180, md: -100 },
              top: -170,
            }}
          />

          <Box
            sx={{
              position: "absolute",
              width: 190,
              height: 190,
              border: "1px solid rgba(151,119,67,0.1)",
              borderRadius: "50%",
              left: { xs: -110, md: -60 },
              bottom: -110,
            }}
          />

          <Container
            maxWidth="lg"
            sx={{
              position: "relative",
              zIndex: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                letterSpacing: "0.3em",
                textTransform: "uppercase",
                color: "#977743",
                fontWeight: 600,
                mb: 2.5,
              }}
            >
              Hindustan Wardrobe · Legal
            </Typography>

            <Typography
              component="h1"
              sx={{
                fontFamily: "Georgia, serif",
                fontWeight: 400,
                fontSize: {
                  xs: 46,
                  sm: 60,
                  md: 76,
                },
                lineHeight: 0.98,
                letterSpacing: "-0.03em",
                maxWidth: 850,
                mb: 3,
              }}
            >
              Terms &
              <br />
              <Box component="span" sx={{ fontStyle: "italic" }}>
                Conditions
              </Box>
            </Typography>

            <Typography
              sx={{
                maxWidth: 650,
                color: "#68635C",
                fontSize: { xs: 14, md: 16 },
                lineHeight: 1.8,
              }}
            >
              Please review these terms carefully before using Hindustan
              Wardrobe. They explain the responsibilities, policies and
              conditions that apply to our rental platform.
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mt: 3,
              }}
            >
              <Box
                sx={{
                  width: 42,
                  height: 1,
                  background: "#977743",
                }}
              />

              <Typography
                sx={{
                  fontSize: 11,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: "#77716A",
                }}
              >
                Effective Date · May 27, 2025
              </Typography>
            </Box>
          </Container>
        </Box>

        <Container
          maxWidth="md"
          sx={{
            py: { xs: 6, md: 9 },
          }}
        >
          <Box
            sx={{
              background: "#171717",
              color: "#fff",
              p: { xs: 3.5, md: 5 },
              mb: { xs: 6, md: 8 },
            }}
          >
            <Typography
              sx={{
                fontSize: 10,
                letterSpacing: "0.25em",
                textTransform: "uppercase",
                color: "#C4A36A",
                mb: 2,
              }}
            >
              Before You Continue
            </Typography>

            <Typography
              sx={{
                fontFamily: "Georgia, serif",
                fontSize: { xs: 24, md: 30 },
                lineHeight: 1.4,
              }}
            >
              Our terms are designed to create a clear and transparent rental
              experience for users and renters.
            </Typography>
          </Box>

          <Stack spacing={0}>
            {sections.map((section, index) => (
              <Box
                key={section.number}
                sx={{
                  pb: 5,
                  mb: 5,
                  borderBottom:
                    index === sections.length - 1
                      ? "none"
                      : "1px solid #DDD7CE",
                }}
              >
                <Stack
                  direction="row"
                  spacing={{ xs: 2, md: 3 }}
                  alignItems="flex-start"
                >
                  <Typography
                    sx={{
                      fontSize: 12,
                      letterSpacing: "0.15em",
                      color: "#977743",
                      fontWeight: 600,
                      minWidth: 30,
                      pt: 0.5,
                    }}
                  >
                    {section.number}
                  </Typography>

                  <Box sx={{ flex: 1 }}>
                    <Typography
                      component="h2"
                      sx={{
                        fontFamily: "Georgia, serif",
                        fontSize: { xs: 27, md: 32 },
                        fontWeight: 400,
                        lineHeight: 1.2,
                        mb: 2.5,
                      }}
                    >
                      {section.title}
                    </Typography>

                    {section.content && (
                      <Typography
                        sx={{
                          color: "#625E58",
                          fontSize: 15,
                          lineHeight: 1.9,
                        }}
                      >
                        {section.content}
                      </Typography>
                    )}

                    {section.intro && (
                      <Typography
                        sx={{
                          color: "#625E58",
                          fontSize: 15,
                          lineHeight: 1.85,
                          mt: 1,
                        }}
                      >
                        {section.intro}
                      </Typography>
                    )}

                    {section.items && renderItems(section.items)}

                    {section.subsections && (
                      <Stack spacing={3.5}>
                        {section.subsections.map((subsection) => (
                          <Box key={subsection.title}>
                            <Typography
                              component="h3"
                              sx={{
                                fontSize: 14,
                                fontWeight: 700,
                                color: "#292929",
                                mb: 1,
                              }}
                            >
                              {subsection.title}
                            </Typography>

                            {subsection.intro && (
                              <Typography
                                sx={{
                                  color: "#625E58",
                                  fontSize: 15,
                                  lineHeight: 1.85,
                                }}
                              >
                                {subsection.intro}
                              </Typography>
                            )}

                            {renderItems(subsection.items)}
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Stack>
              </Box>
            ))}

            <Box
              sx={{
                background: "#EEE9E1",
                p: { xs: 3.5, md: 5 },
                mt: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  color: "#977743",
                  fontWeight: 600,
                  mb: 2,
                }}
              >
                15 · Contact Information
              </Typography>

              <Typography
                component="h2"
                sx={{
                  fontFamily: "Georgia, serif",
                  fontSize: { xs: 28, md: 34 },
                  fontWeight: 400,
                  mb: 2,
                }}
              >
                Need clarification?
              </Typography>

              <Typography
                sx={{
                  color: "#625E58",
                  fontSize: 15,
                  lineHeight: 1.8,
                  mb: 3,
                }}
              >
                For any questions or concerns regarding these Terms and
                Conditions, please contact Hindustan Wardrobe.
              </Typography>

              <Divider sx={{ borderColor: "#D5CEC3", mb: 3 }} />

              <Typography
                sx={{
                  fontSize: 14,
                  color: "#292929",
                }}
              >
                <Box component="span" sx={{ fontWeight: 700 }}>
                  Email:
                </Box>{" "}
                <Box
                  component="a"
                  href="mailto:info@hindustanwardrobe.co"
                  sx={{
                    color: "#977743",
                    textDecoration: "none",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  info@hindustanwardrobe.co
                </Box>
              </Typography>

              <Typography
                sx={{
                  fontSize: 13,
                  color: "#77716A",
                  mt: 2,
                }}
              >
                Account deletion requests can be submitted through our{" "}
                <Link
                  href="/account-delete-request"
                  underline="hover"
                  sx={{
                    color: "#977743",
                    fontWeight: 600,
                  }}
                >
                  Account Deletion Form
                </Link>
                .
              </Typography>
            </Box>
          </Stack>
        </Container>
      </Box>

      {showLayout && <Footer />}
    </>
  );
};

export default TermsConditions;